import React, { useState } from 'react'
import { useSelector } from 'react-redux'
import { useQuery } from '@tanstack/react-query'
import {
	Button, Divider, Drawer, Empty, Modal, notification, Table, Tag
} from 'antd'
import { FilePdfOutlined, PlusOutlined } from '@ant-design/icons'
import { useTranslation } from 'react-i18next'
import { ColumnsType } from 'antd/lib/table'
import { RootState } from '../../reducers'
import StoreItems from '../storeItems/StoreItems'
import { StoreItemTableItem } from '../../reducers/storeItems/actions'
import {
	fetchHandoverProtocols, fetchHandoverProtocolPdfUrl, resendHandoverProtocolEmail, voidHandoverProtocol
} from './handoverProtocolApi'
import { HandoverProtocolRow, HandoverProtocolType } from './types'
import {
	canVoidProtocol, formatProtocolDate, getEmailStatus
} from './handoverProtocolRows'
import PersonalProtocolsSection from './PersonalProtocolsSection'
import HandoverProtocolPreview from './HandoverProtocolPreview'

type Props = {
	visible: boolean
	onClose: () => void
	type: HandoverProtocolType
	jobID?: number | null
	// pre PERSONAL = vlastnik osobneho skladu (majitel profilu), nikdy nie pripadny zaskok, ktory protokol podpisal
	userID?: number | null
	title: string
	// spodna tabulka - presne tie props, ktore uz dnes posiela hostitelska stranka do <StoreItems>
	storeItemsProps: {
		tableData: StoreItemTableItem[] | null | undefined
		initFilter: { jobID: number | null, jobToID: number | null | undefined }
	}
	// predvyplneny preberajuci pre novy protokol - pracovnik priradeny na zakazku (JOB), resp. vlastnik
	// osobneho skladu (PERSONAL); manazer ho v nahlade este vie prepnut
	defaultRecipientUserID?: number | null
	defaultRecipientName?: string | null
}

const HandoverProtocolsDrawer: React.FC<Props> = ({
	visible, onClose, type, jobID, userID, title, storeItemsProps, defaultRecipientUserID, defaultRecipientName
}) => {
	const { t } = useTranslation('pages')
	const profile = useSelector((state: RootState) => state.profile.profile.data)
	const [isPreviewVisible, setIsPreviewVisible] = useState(false)

	const { data: protocols = [], isLoading, refetch } = useQuery({
		queryKey: ['handoverProtocols', type, jobID, userID],
		queryFn: () => (type === 'JOB'
			? fetchHandoverProtocols({ type: 'JOB', jobID: jobID as number })
			: fetchHandoverProtocols({ type: 'PERSONAL', userID: userID as number })),
		enabled: visible && !!(type === 'JOB' ? jobID : userID)
	})

	const openProtocolPdf = async (id: number) => {
		// tab treba otvorit este v ramci kliknutia, inak ho prehliadac zablokuje ako vyskakovacie okno
		const tab = window.open('', '_blank')
		try {
			const pdfUrl = await fetchHandoverProtocolPdfUrl(id)
			if (tab) {
				tab.location.href = pdfUrl
			} else {
				window.location.href = pdfUrl
			}
		} catch (e) {
			if (tab) tab.close()
			notification.error({ message: t('pages:PDF protokolu sa nepodarilo otvoriť') as string })
		}
	}

	const handleResendEmail = async (id: number) => {
		try {
			await resendHandoverProtocolEmail(id)
			refetch()
		} catch (e) {
			// resendHandoverProtocolEmail uz zobrazuje vlastnu notifikaciu (NOTIFICATION_TYPE.NOTIFICATION)
		}
	}

	const handleVoidProtocol = (id: number) => {
		Modal.confirm({
			title: t('pages:Naozaj stornovať tento preberací protokol?') as string,
			content: t('pages:Protokol zmizne zo zoznamu. Súbory ostanú uložené.') as string,
			okText: t('pages:Stornovať') as string,
			okType: 'danger',
			cancelText: t('pages:Zrušiť') as string,
			onOk: async () => {
				await voidHandoverProtocol(id)
				refetch()
			}
		})
	}

	const showVoidColumn = canVoidProtocol(profile)

	const columns: ColumnsType<HandoverProtocolRow> = [
		{
			title: t('pages:Dátum') as string,
			key: 'signedAt',
			render: (_, row) => formatProtocolDate(row.signedAt)
		},
		{
			title: t('pages:Zamestnanec') as string,
			dataIndex: 'recipientName',
			key: 'recipientName'
		},
		{
			title: t('pages:Manažér') as string,
			dataIndex: 'createdByName',
			key: 'createdByName'
		},
		{
			title: t('pages:Počet položiek') as string,
			dataIndex: 'itemsCount',
			key: 'itemsCount'
		},
		{
			title: t('pages:Suma') as string,
			key: 'totalPrice',
			render: (_, row) => (row.totalPrice != null ? `${row.totalPrice.toFixed(2)} €` : '-')
		},
		{
			title: t('pages:E-mail') as string,
			key: 'emailStatus',
			render(_, row) {
				const status = getEmailStatus(row)
				return (
					<div className={'flex align-center'}>
						<Tag color={status.color}>{status.label}</Tag>
						{status.canResend && (
							<Button
								size={'small'}
								type={'link'}
								onClick={(e) => {
									e.stopPropagation()
									handleResendEmail(row.id)
								}}
							>
								{t('pages:Poslať znova')}
							</Button>
						)}
					</div>
				)
			}
		},
		{
			title: t('pages:PDF') as string,
			key: 'pdf',
			render(_, row) {
				return (
					<Button
						size={'small'}
						icon={<FilePdfOutlined />}
						onClick={(e) => {
							e.stopPropagation()
							openProtocolPdf(row.id)
						}}
					/>
				)
			}
		},
		...(showVoidColumn ? [{
			title: '',
			key: 'actions',
			render(_: any, row: HandoverProtocolRow) {
				return (
					<Button
						size={'small'}
						danger
						onClick={(e: React.MouseEvent) => {
							e.stopPropagation()
							handleVoidProtocol(row.id)
						}}
					>
						{t('pages:Storno')}
					</Button>
				)
			}
		}] : [])
	]

	return (
		<Drawer
			visible={visible}
			onClose={onClose}
			width={'90%'}
			placement={'right'}
			title={`${t('pages:Preberacie protokoly')} — ${title}`}
			destroyOnClose
		>
			{/* Tlacidlo patri do tela, nie do Drawer.extra - ten prop pribudol az v antd 4.17
			    a v 4.15 sa ticho ignoruje, takze sa nevykreslilo vobec. */}
			<div className={'flex justify-between items-center mb-4'}>
				<h3 className={'mb-0'}>{t('pages:Podpísané protokoly')}</h3>
				<Button type={'primary'} icon={<PlusOutlined />} onClick={() => setIsPreviewVisible(true)}>
					{t('pages:Nový preberací protokol')}
				</Button>
			</div>
			{protocols.length === 0 && !isLoading ? (
				<Empty description={t('pages:Zatiaľ žiadne podpísané protokoly') as string} />
			) : (
				<Table
					size={'small'}
					rowKey={'id'}
					dataSource={protocols}
					loading={isLoading}
					pagination={false}
					columns={columns}
					onRow={(row) => ({
						onClick: () => openProtocolPdf(row.id),
						style: { cursor: 'pointer' }
					})}
				/>
			)}

			<Divider />

			<h3>{type === 'JOB' ? t('pages:Aktuálne na zákazke') : t('pages:Aktuálne osobné veci')}</h3>

			<StoreItems
				tableMode
				tableData={storeItemsProps.tableData}
				initFilter={storeItemsProps.initFilter}
				addRemoveItemFeature={false}
				contextStore={null}
			/>

			{/* Tretia tabulka len v panele zakazky: osobne veci pracovnika, ktory je na nej
			    priradeny. Osobny sklad patri jeho profilu, tu je len na docitanie a upozornenie. */}
			{type === 'JOB' && defaultRecipientUserID && (
				<>
					<Divider />
					<PersonalProtocolsSection
						userID={defaultRecipientUserID}
						userName={defaultRecipientName ?? null}
						onOpenProtocol={openProtocolPdf}
					/>
				</>
			)}

			<HandoverProtocolPreview
				visible={isPreviewVisible}
				type={type}
				jobID={jobID}
				userID={userID}
				title={title}
				defaultRecipientUserID={defaultRecipientUserID ?? null}
				defaultRecipientName={defaultRecipientName ?? null}
				storeItemsProps={storeItemsProps}
				onCancel={() => setIsPreviewVisible(false)}
				onCreated={() => { setIsPreviewVisible(false); refetch() }}
			/>
		</Drawer>
	)
}

export default HandoverProtocolsDrawer
